import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Cell, Pie, PieChart, Tooltip, TooltipProps } from 'recharts';

import { ReactComponent as NavigateArrow } from '../../../../assets/icons/dashboard/navigate-arrow.svg';
import { appCurrentBranding } from '../../../../config/branding';
import { branding, connectorStatus } from '../../../../config/enum';
import { useAppDispatch } from '../../../../redux/hooks';
import { setNotificationDetails } from '../../../../redux/slices/NotificationSlice';
import { updateDeviceFilters } from '../../../../redux/slices/Partners/DeviceManagementSlice';
import BatteryLoader from '../../../../shared-components/battery-loader/BatteryLoader';
import dashboard from '../../../../utils/api/partner/dashboard';

type PieChartDataType = {
  name: string;
  stableKey: string;
  value: number;
  color: string;
};

/**
 * Connector Status
 * @returns {any} The result
 */
export default function ConnectorStatusChart() {
  const { t } = useTranslation('dashboard');
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [pieChartData, setPieChartData] = useState<PieChartDataType[]>([
    {
      name: connectorStatus.CHARGING,
      stableKey: connectorStatus.CHARGING,
      value: 0,
      color: 'var(--charging-color)',
    },
    {
      name: connectorStatus.AVAILABLE,
      stableKey: connectorStatus.AVAILABLE,
      value: 0,
      color: 'var(--available-color)',
    },
    {
      name: connectorStatus.PREPARING,
      stableKey: connectorStatus.PREPARING,
      value: 0,
      color: 'var(--preparing-color)',
    },
    {
      name: connectorStatus.UNAVAILABLE,
      stableKey: connectorStatus.UNAVAILABLE,
      value: 0,
      color: 'var(--unavailable-color)',
    },
    {
      name: connectorStatus.FINISHING,
      stableKey: connectorStatus.FINISHING,
      value: 0,
      color: 'var(--finishing-color)',
    },
    {
      name: connectorStatus.FAULTED,
      stableKey: connectorStatus.FAULTED,
      value: 0,
      color: 'var(--faulted-color)',
    },
    {
      name: connectorStatus.SUSPENDED_EV,
      stableKey: connectorStatus.SUSPENDED_EV,
      value: 0,
      color: 'var(--suspendedEV-color)',
    },
    {
      name: connectorStatus.SUSPENDED_EVSE,
      stableKey: connectorStatus.SUSPENDED_EVSE,
      value: 0,
      color: 'var(--suspendedEVSE-color)',
    },
    { name: t('Others'), stableKey: 'Others', value: 0, color: 'var(--unknown-color)' },
  ]);

  /**
   * Function to handle redirect device management page with filter.
   * @param {string} key - The filter key.
   * @returns {void}
   */
  const handleClickPieChartCell = (key: string): void => {
    if (appCurrentBranding === branding.VOLTIE) {
      let valueForFilter: string[] = [key];
      if (key === 'Others') {
        valueForFilter = [
          connectorStatus.PREPARING,
          connectorStatus.RESERVED,
          connectorStatus.UNAVAILABLE,
        ];
      }
      dispatch(
        updateDeviceFilters({
          key: 'connectorStatus',
          value: valueForFilter,
          type: 'restAndAdd',
        }),
      );
      navigate({
        pathname: '/ev-admin/operations/chargers',
        search: `?${new URLSearchParams({
          connectorStatus: valueForFilter.join(','),
        })}`,
      });
    }
  };

  /**
   * Function to fetch connector status report
   */
  const fetchConnectorStatusReport = () => {
    setIsLoading(true);
    dashboard
      .getConnectorStatusReport()
      .then((response) => {
        const result = Array.isArray(response?.data) ? response.data : [];

        setPieChartData((previousData: any) =>
          previousData.map((item: any) => {
            const found = result.find(
              (r: any) => r?.status?.toLowerCase() === item.stableKey.toLowerCase(),
            );
            return {
              ...item,
              name: item.stableKey === 'Others' ? t('Others') : item.stableKey,
              value: found ? found.count : 0,
            };
          }),
        );
        setIsLoading(false);
      })
      .catch((error) => {
        setIsLoading(false);
        dispatch(
          setNotificationDetails({
            type: 'error',
            notificationDetails: {
              statusCode: error?.response?.data?.code || 200,
              message: error?.response?.data?.message || t('somethingWentWrong'),
            },
          }),
        );
      });
  };

  /**
   * Function to show custom tooltip
   * @param {any} param0 - The param0
   * @param {any} param0.active - The active prop
   * @param {any} param0.payload - The payload prop
   * @returns {any} The result
   */
  function CustomTooltip({ active, payload }: TooltipProps<number, string>) {
    if (active && payload && payload.length > 0) {
      const data = payload[0];

      return (
        <div className="connector-status-custom-tooltip-partner">
          <p className="label" style={{ color: data?.payload?.color }}>
            {data?.payload?.name ? `${data.payload.name} : ${data.value}` : ''}
          </p>
        </div>
      );
    }

    return null;
  }

  useEffect(() => {
    fetchConnectorStatusReport();
  }, []);

  return (
    <div className="connector-status-chart-wrap-partner">
      <div className="connector-status-chart-header">
        <div className="header-title">
          <h6>{t('connectorStatus')}</h6>
          <span
            className="header-arrow"
            role="presentation"
            onClick={() => navigate('/ev-admin/operations/chargers')}>
            <NavigateArrow />
          </span>
        </div>
      </div>
      {isLoading ? (
        <center className="connector-status-loader">
          <BatteryLoader />
        </center>
      ) : (
        <>
          <div className="connector-status-chart-container">
            <PieChart width={250} height={250}>
              <Pie
                data={pieChartData}
                cx={125}
                cy={125}
                innerRadius={60}
                outerRadius={100}
                fill="#8884d8"
                paddingAngle={2}
                dataKey="value">
                {pieChartData.map((entry) => (
                  <Cell
                    key={entry.stableKey}
                    fill={entry.color}
                    style={{ outline: 'none', cursor: 'pointer' }}
                    onClick={() => handleClickPieChartCell(entry.stableKey)}
                  />
                ))}
              </Pie>
              <Tooltip content={<CustomTooltip />} />
            </PieChart>
            <div className="total-sessions-wrap">
              <p>
                {t('total')} <br /> {t('connectors')}
              </p>
              <span>{pieChartData.reduce((sum, item) => sum + item.value, 0)}</span>
            </div>
          </div>
          <div className="pie-chart-label">
            {pieChartData.map((eachData) => (
              <div className="each-label" key={eachData.stableKey}>
                <div
                  className="each-label-color"
                  style={{ '--label-color': eachData.color } as React.CSSProperties}
                />
                <p
                  role="presentation"
                  onClick={() => handleClickPieChartCell(eachData.stableKey)}
                  style={
                    appCurrentBranding === branding.VOLTIE ? { cursor: 'pointer' } : undefined
                  }>
                  {eachData.name}
                </p>
                <div className="each-label-value">
                  <span>{eachData.value}</span>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
